import { PropsWithChildren } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import {
  CustomDialogContent,
  CustomDialogFooter,
  CustomDialogTitle,
} from "../ui/custom-dialog";
import { Dialog, DialogHeader } from "../ui/dialog";
import { Form } from "../ui/form";
import { useAuth } from "@/context/auth/AuthContext";

type NewItemDialogProps<T extends FieldValues> = PropsWithChildren<{
  title: string;
  onFormSubmit: (data: T) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<T>;
  isLoading?: boolean;
}>;

const NewItemDialog = <T extends FieldValues>({
  children,
  title,
  onFormSubmit,
  open,
  onOpenChange,
  form,
  isLoading = false,
}: NewItemDialogProps<T>) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isReader = user?.role === "reader" ? true : false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <CustomDialogContent>
        <DialogHeader>
          <CustomDialogTitle>{title}</CustomDialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onFormSubmit)}>
            <div className="px-6 flex flex-col gap-6 mb-8 mt-3">
              {children}
            </div>

            <CustomDialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex flex-1 bg-white"
              >
                {t("actionCancel")}
              </Button>
              <Button
                type="submit"
                className="flex flex-1"
                loading={isLoading}
                disabled={isReader}
              >
                {t("add")}
              </Button>
            </CustomDialogFooter>
          </form>
        </Form>
      </CustomDialogContent>
    </Dialog>
  );
};

export default NewItemDialog;
